import { CHECKLIST_SIGN_OFF_STATE } from '../../../common/utils/checklist/checklistSignOff';
import { MY_DAY_STEP_STATUS } from '../../../common/utils/myDay/driverDaySteps';
import { STEP_STATUS } from '../../design/components/StatusIcon';

/**
 * Design StepRow status for a phase step from driverDaySteps (start/end of day):
 * done -> done, blocked -> blocked, the next action's step -> current, else todo
 * (to do and info steps). Hidden steps are filtered out before rendering.
 */
export const getPhaseStepStatus = (step, nextStepKey) => {
	if (step.status === MY_DAY_STEP_STATUS.DONE) {
		return STEP_STATUS.DONE;
	}
	if (step.status === MY_DAY_STEP_STATUS.BLOCKED) {
		return STEP_STATUS.BLOCKED;
	}

	return step.key === nextStepKey ? STEP_STATUS.CURRENT : STEP_STATUS.TODO;
};

/**
 * Design StepRow status for a stop step (buildPerStopSteps / day.buildStopSteps):
 * done -> done, blocked -> blocked, the first open step -> current, else todo.
 */
export const getStopStepStatus = (step, nextStepId) => {
	if (step.done) {
		return STEP_STATUS.DONE;
	}
	if (step.blocked) {
		return STEP_STATUS.BLOCKED;
	}

	return step.id === nextStepId ? STEP_STATUS.CURRENT : STEP_STATUS.TODO;
};

const OPTIONAL_TAGS = [{ label: 'Optional' }];

/**
 * StepRow tags for a phase step the driver may skip (not required by the
 * tenant) while it is still open; undefined otherwise. Step logic is unchanged:
 * the next action still skips optional steps.
 */
export const getOptionalStepTags = step => (
	!step.required && [MY_DAY_STEP_STATUS.TODO, MY_DAY_STEP_STATUS.INFO].includes(step.status)
		? OPTIONAL_TAGS
		: undefined
);

/**
 * Progress for the Start of day and End of day screens over the REQUIRED steps
 * only (open optional steps carry an "Optional" tag and are not counted), so
 * both screens count the same thing and say so: "2 of 4 required steps done".
 * Training-locked and info-only steps are left out. Returns
 * { doneCount, totalCount, label }; totalCount 0 when nothing is required.
 */
export const getRequiredStepProgress = (steps) => {
	const required = steps.filter(step => (
		step.required &&
		!step.trainingUnavailable &&
		[MY_DAY_STEP_STATUS.DONE, MY_DAY_STEP_STATUS.TODO, MY_DAY_STEP_STATUS.BLOCKED].includes(step.status)
	));
	const doneCount = required.filter(step => step.status === MY_DAY_STEP_STATUS.DONE).length;

	return {
		doneCount,
		totalCount: required.length,
		label: `${doneCount} of ${required.length} required ${required.length === 1 ? 'step' : 'steps'} done`,
	};
};

/**
 * Right-hand StepRow text for a done checklist step that needs manager sign-off
 * (done rows hide their description): { statusText, statusTone }, or undefined.
 */
export const getSignOffStepStatus = (step) => {
	if (step.signOffState === CHECKLIST_SIGN_OFF_STATE.WAITING) {
		return { statusText: 'Waiting for sign-off', statusTone: 'warning' };
	}
	if (step.signOffState === CHECKLIST_SIGN_OFF_STATE.SIGNED_OFF) {
		return { statusText: 'Signed off' };
	}

	return undefined;
};
